import { useEffect, useRef, useState } from "react";

/**
 * Rolling [min, max] domain for the timeline's live view.
 *
 * The right edge is an advancing "now" anchored to the wall clock
 * (`Date.now()`), ticking once per `tickMs`. The edge is monotonic — it only
 * ever moves forward — so the view never slides backwards (e.g. after a
 * suspended tab catches up).
 *
 * Two anchoring modes:
 * - `now`-anchored (default): the window tracks `Date.now()`, monotonic, with
 *   `leadFraction` (0–1) reserving that fraction of the span as empty "future"
 *   space to the right so `now` lands at `(1 - leadFraction)` of the width
 *   (e.g. 0.2 → 80%). Default `0` pins `now` to the right edge (100%).
 * - CURSOR-anchored (map live-follow): pass `anchorFraction`. The window instead
 *   tracks `anchorMs` (the shared cursor = the video's live playback position),
 *   pinning it at `anchorFraction` of the width (e.g. 0.75 → cursor at 75%). The
 *   ~(1 - anchorFraction) to its right holds the newest events, which run ahead
 *   of the cursor by the footage-upload lag. Falls back to `Date.now()` until the
 *   cursor exists; NOT monotonic (the cursor legitimately steps back once as
 *   playback catches from the event-edge seed onto real footage, and the view
 *   should follow it). Sampled at `tickMs` so it advances smoothly (via the event
 *   rects' CSS transitions) rather than re-computing every playback frame; the
 *   cursor line itself moves per-frame off `selectedTime`, so it stays pinned.
 *
 *   The right edge is nevertheless FLOORED at wall-clock now: the cursor is a
 *   playback position and it stalls (a backgrounded tab pauses rAF, a footage gap
 *   or a pause stops playback, going live can leave it minutes behind). Anchoring
 *   a stalled cursor freezes the window in the past while events keep arriving —
 *   the view reads as stuck, and once the lag exceeds the fetch window's lead the
 *   left of the view holds data that was never fetched, showing as a widening
 *   empty stretch. Flooring the edge means the window follows the clock as soon
 *   as the cursor lags by more than the right-hand lead, and the cursor simply
 *   slides left inside the window (still visible) instead of dragging it back.
 *   This also bounds `min` at `now - spanMs`, exactly what the rolling fetch
 *   window covers, so the view can never outrun the data.
 *
 * Returns null while disabled. `paused` freezes the advance (e.g. during edit
 * mode or alert review) without dropping the domain.
 */
export const useLiveTimelineDomain = ({
  enabled,
  spanMs,
  paused = false,
  tickMs = 1000,
  leadFraction = 0,
  anchorMs = null,
  anchorFraction = null,
}: {
  enabled: boolean;
  spanMs: number;
  paused?: boolean;
  tickMs?: number;
  leadFraction?: number;
  /** Cursor position (ms) to anchor on when `anchorFraction` is set. */
  anchorMs?: number | null;
  /** When set (0–1), anchor on the cursor at this fraction instead of `now`. */
  anchorFraction?: number | null;
}): { min: number; max: number } | null => {
  const cursorAnchored = anchorFraction != null;
  // Width reserved to the RIGHT of the anchor — also the maximum cursor lag the
  // window absorbs before it starts following the clock instead.
  const leadMs = cursorAnchored ? spanMs * (1 - (anchorFraction ?? 0)) : 0;
  // Latest cursor, read on tick (not a dep) so playback frames don't re-run the
  // interval.
  const anchorRef = useRef(anchorMs);
  anchorRef.current = anchorMs;
  const [value, setValue] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setValue(null);
      return;
    }
    if (paused) return;
    const tick = () => {
      setValue((prev) => {
        const now = Date.now();
        if (cursorAnchored) {
          // Follow the cursor (video position); Date.now() until it exists. The
          // floor keeps `now` inside the window when the cursor stalls or lags —
          // see the note on cursor anchoring above.
          const cursor = anchorRef.current;
          return cursor == null ? now : Math.max(cursor, now - leadMs);
        }
        // Monotonic edge: never move backwards.
        return prev !== null && now < prev ? prev : now;
      });
    };
    tick();
    const id = setInterval(tick, tickMs);
    return () => clearInterval(id);
  }, [enabled, paused, tickMs, cursorAnchored, leadMs]);

  if (!enabled || value === null) return null;
  if (anchorFraction != null) {
    // Pin the cursor at `anchorFraction` of the width; the rest to its right holds
    // the newest (video-less) events up to `now`.
    return {
      min: value - spanMs * anchorFraction,
      max: value + spanMs * (1 - anchorFraction),
    };
  }
  // now-anchored: `now` sits at (1 - leadFraction) of the width.
  return {
    min: value - spanMs * (1 - leadFraction),
    max: value + spanMs * leadFraction,
  };
};
